import { useCoverGeniusSeeMoreCoversState } from "../useCoverGeniusSeeMoreCoversState";
import { useEffect, useMemo, useRef } from "react";
import type { QuestionProps } from "../../components/Questionnaire/Section/Question/types";
import { isQuestion } from "../../components/Questionnaire/Section/Question/types";
import {
  useQuestionnaireActions,
  useVisibilityMap,
} from "../../features/questionnaire";
import { useSnowplowContext } from "@simplybusiness/services";

export const getVisibleQuestions = (allQuestions: QuestionProps[]) =>
  allQuestions.filter(isQuestion).filter(({ visible }) => visible);

export const getSuggestedQuestions = (allQuestions: QuestionProps[]) =>
  allQuestions.filter(isQuestion).filter(({ suggested }) => suggested);

/**
 * Hook to manage CoverGenius "see more covers" functionality.
 *
 * This hook manages question visibility through questionnaire actions
 * based on the "see more" state:
 * - When in limited view: only suggested questions are visible
 * - When expanded: all visible questions are shown
 *
 * On mount, if there are more visible questions than suggested ones:
 * - Initializes state to show only suggested questions (limited view)
 * - User can then click "see more" to expand and see all questions
 *
 * Otherwise, shows all questions immediately (no "see more" needed).
 */
export const useCoverGenius = ({
  questions,
}: {
  questions: QuestionProps[];
}) => {
  const [showOnlySuggested, setShowOnlySuggested] =
    useCoverGeniusSeeMoreCoversState();
  const { setVisible, setHidden } = useQuestionnaireActions();
  const { snowplow } = useSnowplowContext();
  const visibilityMap = useVisibilityMap();
  const trackedQuestions = useRef(new Set<string>());
  const hasTrackedInitialCovers = useRef(false);

  const suggestedQuestions = useMemo(
    () => getSuggestedQuestions(questions),
    [questions],
  );

  const visibleQuestions = useMemo(
    () => getVisibleQuestions(questions),
    [questions],
  );

  // Initialize state on mount only (not when questions change)
  useEffect(() => {
    // If there are additional questions beyond suggested ones, enable "see more" mode
    if (suggestedQuestions.length < visibleQuestions.length) {
      // Start in limited view (showing only suggested questions)
      setShowOnlySuggested(true);
    } else {
      // Not enough questions to warrant "see more", show all immediately
      setShowOnlySuggested(false);
    }
    // Empty deps intentional - only initialize once on mount, not when questions change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update question visibility based on "see more" state
  useEffect(() => {
    const questionNames = showOnlySuggested
      ? suggestedQuestions.map(q => q.name)
      : visibleQuestions.map(q => q.name);

    setVisible(questionNames);

    // Only hide non-suggested primary questions when showing suggested only
    // A question is primary if it has display.type === "personalised_cover"
    if (showOnlySuggested) {
      const toHide = visibleQuestions
        .filter(q => !suggestedQuestions.some(sq => sq.name === q.name))
        .filter(q => q.display?.type === "personalised_cover")
        .map(q => q.name);

      if (toHide.length > 0) {
        setHidden(toHide);
      }
    }
  }, [
    showOnlySuggested,
    suggestedQuestions,
    visibleQuestions,
    visibilityMap,
    setVisible,
    setHidden,
  ]);

  // Track coverPresented events for newly visible covers
  // Strategy:
  // 1. On initial mount: ONLY track suggested covers that are visible (suggested=true AND visible=true)
  // 2. On subsequent updates when "see more" is expanded: Track additional non-suggested visible covers
  useEffect(() => {
    const visibleNamesSet = new Set(visibleQuestions.map(q => q.name));

    // On first render, ONLY track visible suggested covers
    if (!hasTrackedInitialCovers.current) {
      suggestedQuestions.forEach(question => {
        if (visibleNamesSet.has(question.name)) {
          snowplow?.trigger("coverPresented", {
            property: question.primaryText || question.name,
          });
          trackedQuestions.current.add(question.name);
        }
      });

      hasTrackedInitialCovers.current = true;
      return; // Exit early on first render - don't track non-suggested covers
    }

    // On subsequent renders, if expanded view, track non-suggested visible covers
    if (!showOnlySuggested) {
      const suggestedNamesSet = new Set(suggestedQuestions.map(q => q.name));

      visibleQuestions.forEach(question => {
        if (
          !suggestedNamesSet.has(question.name) &&
          !trackedQuestions.current.has(question.name)
        ) {
          snowplow?.trigger("coverPresented", {
            property: question.primaryText || question.name,
          });
          trackedQuestions.current.add(question.name);
        }
      });
    }
  }, [showOnlySuggested, suggestedQuestions, visibleQuestions, snowplow]);
};
